import React, { useEffect, useState } from "react";
import UserTable from "../components/UserTable";
import SearchBar from "../components/SearchBar";

const formatDate = (date) => {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, "0"); // Convert month to 2 digits
  const day = `${d.getDate()}`.padStart(2, "0"); // Convert day to 2 digits
  return [d.getFullYear(), month, day].join("-");
};

// Helper function to subtract months from a date
const subtractMonths = (numOfMonths, date = new Date()) => {
  date.setMonth(date.getMonth() - numOfMonths);
  // Handles cases where the month has fewer days
  date.setDate(Math.min(date.getDate(), new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
  return date;
};

// Calculate the date for three months ago
const threeMonthsAgo = formatDate(subtractMonths(3));
const limit = 10000;

function ProjectSearch({ projectId, searchResults, onBookmarkToggle, setSearchResults }) {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [cutOffDate, setCutOffDate] = useState(threeMonthsAgo);
  const [queryExact, setQueryExact] = useState("");
  const [querySemantic, setQuerySemantic] = useState("");
  const [results, setResults] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [ethnicity, setEthnicities] = useState([]);
  const [ageValues, setAgeValues] = useState([]);
  const [marital, setMarital] = useState([]);
  const [occupation, setOccupation] = useState("");
  const [occupationFuzzy, setOccupationFuzzy] = useState("");
  const [market, setMarket] = useState([]);
  const [marketFuzzy, setMarketFuzzy] = useState("");
  const [gender, setGender] = useState("");
  const [genderFuzzy, setGenderFuzzy] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [blacklist, setBlacklist] = useState(false);
  const [participated, setParticipated] = useState([]);
  const [classes, setClasses] = useState([]);
  const [badclasses, setBadClasses] = useState([]);

  const [clients, setClient] = useState("");
  const [incomeValues, setIncomeValues] = useState([]);

  const host = process.env.REACT_APP_SERVER;

  const onSearch = async (newPage) => {
    console.log("onSearch", "newPage", newPage);
    if (querySemantic && querySemantic.trim() !== "") {
      await semanticSearch(querySemantic);
    } else {
      exactSearch(newPage);
    }
  };

  const semanticSearch = async () => {
    console.log("FETCHING DATA");
    setLoading(true);
    const url = "https://satori-gpt4.openai.azure.com/openai/deployments/satori-embeddings/embeddings?api-version=2023-05-15";
    try {
      const embeddingsResponse = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "api-key": process.env.REACT_APP_OPENAI_API_KEY,
        },
        body: JSON.stringify({
          input: querySemantic,
          model: process.env.REACT_APP_OPENAI_MODEL,
        }),
      });

      const embeddingsData = await embeddingsResponse.json();
      const vector = embeddingsData.data[0].embedding;
      const filter = { blacklist };

      if (classes && classes.length > 0) {
        filter["$or"] = classes.map((d) => {
          return { participation: d.value };
        });
      }

      if (badclasses && badclasses.length > 0) {
        if (classes && classes.length > 0) {
          filter["$and"] = [{ $or: classes.map((d) => ({ participation: d.value })) }, { participation: { $nin: badclasses.map((badClass) => badClass.value) } }];
        } else {
          filter.participation = { $nin: badclasses.map((badClass) => badClass.value) };
        }
      }

      if (ethnicity.length > 0) {
        console.log("ethnicity", ethnicity);
        filter.ethnicity = { $in: ethnicity.map((e) => e.value.toLowerCase()) };
      }
      if (gender) {
        filter.gender = gender.toLowerCase();
      }
      if (marital.length > 0) {
        // filter.marital_status = marital.toLowerCase();
        filter.marital_status = { $in: marital };
      }
      if (market.length > 0) {
        filter.market = { $in: market };
      }
      if (occupation) {
        filter.occupation = occupation.toLowerCase();
      }
      if (occupationFuzzy) {
        filter.occupation_self_described = occupationFuzzy.toLowerCase();
      }
      if (marketFuzzy) {
        filter.market_self_described = marketFuzzy.toLowerCase();
      }
      if (genderFuzzy) {
        filter.gender_self_described = genderFuzzy.toLowerCase();
      }
      if (clients) {
        filter.clients = clients;
      }
      if (ageValues[0]) {
        const year = new Date().getFullYear();
        filter.year_of_birth = { $gte: year - ageValues[1], $lte: year - ageValues[0] };
      }
      if (incomeValues[0]) {
        filter.household_income = { $gte: incomeValues[0], $lte: incomeValues[1] };
      }
      console.log("filter", filter);

      const pineconeResponse = await fetch("https://9x02apw5e6.execute-api.us-east-1.amazonaws.com/default/pinecone", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ vector, filter, topK: 1000 }),
      });

      const pineconeData = await pineconeResponse.json();

      // Extract user IDs from Pinecone matches
      const emailAddresses = pineconeData.matches.map((match) => match.metadata.email_address);

      console.log(`${process.env.REACT_APP_SERVER}/api/users`);

      const userDataResponse = await fetch(`${process.env.REACT_APP_SERVER}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emailAddresses: emailAddresses, projectId: projectId, cutOffDate }), // Send the user IDs to the server as integers
      });

      const userData = await userDataResponse.json();
      console.log("userData", userData);
      setTotal(userData.data.users.length);
      setResults(userData.data.users);
      setSearchResults(userData.data.users);

      //   const pineconeData = await pineconeResponse.json();
      //   setResults(pineconeData.matches);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // This function is for the client-side to call the server-side endpoint
  const exactSearch = async (page) => {
    let params = {
      userName,
      queryExact,
      ethnicity: ethnicity.map((d) => d.value),
      market: market.map((d) => d.value),
      marital: marital.map((d) => d.value),
      gender,
      occupation,
      occupationFuzzy,
      marketFuzzy,
      genderFuzzy,
      ageValues,
      incomeValues,
      cutOffDate,
      classes: classes.map((d) => d.value),
      badclasses: badclasses.map((d) => d.value),
      blacklist,
    };
    try {
      console.log(`${process.env.REACT_APP_SERVER}/api/exactSearch`);
      const response = await fetch(`${process.env.REACT_APP_SERVER}/api/exactSearch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...params, page, limit }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userData = await response.json();
      console.log("userData", userData);
      setTotal(userData.data.total || userData.data.users.length);
      setResults(userData.data.users);
      setSearchResults(userData.data.users);
      // setPage(newPage);
      // console.log("Exact search results:", result);
      // Handle your results here
    } catch (error) {
      console.error("Error during exact search:", error);
      // Handle your error here
    }
  };

  return (
    <div className="w-full p-5">
      <div className="container mx-auto text-center">
        <SearchBar
          onSearch={onSearch}
          userName={userName}
          setUserName={setUserName}
          querySemantic={querySemantic}
          setQuerySemantic={setQuerySemantic}
          queryExact={queryExact}
          setQueryExact={setQueryExact}
          cutOffDate={cutOffDate}
          setCutOffDate={setCutOffDate}
          gender={gender}
          setGender={setGender}
          genderFuzzy={genderFuzzy}
          setGenderFuzzy={setGenderFuzzy}
          ethnicity={ethnicity}
          setEthnicities={setEthnicities}
          marital={marital}
          setMarital={setMarital}
          occupation={occupation}
          setOccupation={setOccupation}
          occupationFuzzy={occupationFuzzy}
          setOccupationFuzzy={setOccupationFuzzy}
          marketFuzzy={marketFuzzy}
          setMarketFuzzy={setMarketFuzzy}
          market={market}
          setMarket={setMarket}
          setClient={setClient}
          ageValues={ageValues}
          setAgeValues={setAgeValues}
          setIncomeValues={setIncomeValues}
          blacklist={blacklist}
          setBlacklist={setBlacklist}
          participated={participated}
          setParticipated={setParticipated}
          classes={classes}
          setClasses={setClasses}
          badclasses={badclasses}
          setBadClasses={setBadClasses}
          loading={loading}
          setPage={setPage}
        />
        <p>{`${total} results found`}</p>
        {loading ? <p>Loading...</p> : null}
        {searchResults.length > 0 ? (
          <div>
            <UserTable users={searchResults} projectId={projectId} onBookmarkToggle={onBookmarkToggle} isCurrentList={false} />

            <button
              className="btn btn-primary ml-2"
              onClick={() => {
                const newPage = page - 1;
                setPage(newPage);
                onSearch(newPage);
              }}
            >
              Previous
            </button>
            <button
              className="btn btn-primary ml-2"
              onClick={() => {
                const newPage = page + 1;
                setPage(newPage);
                onSearch(newPage);
              }}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ProjectSearch;
